/**
 * BetaStatusMessage - Display beta access denial messages
 *
 * Reads from sessionStorage where AuthContext stores denial info after forced sign-out
 */

import { useEffect, useState } from 'react';
import { ExclamationTriangleIcon, ClockIcon, XCircleIcon } from '@heroicons/react/24/outline';

interface BetaStatusMessageProps {
  className?: string;
}

export const BetaStatusMessage = ({ className = '' }: BetaStatusMessageProps) => {
  const [denial, setDenial] = useState<{ code: string; message: string } | null>(null);

  useEffect(() => {
    const stored = sessionStorage.getItem('beta_access_denial');
    if (stored) {
      try {
        setDenial(JSON.parse(stored));
        // Clear it after reading so it doesn't persist
        sessionStorage.removeItem('beta_access_denial');
      } catch (error) {
        console.error('Failed to parse beta_access_denial from sessionStorage:', error);
      }
    }
  }, []);

  if (!denial) return null;

  const getIcon = () => {
    switch (denial.code) {
      case 'BETA_PENDING':
        return <ClockIcon className="w-6 h-6 text-yellow-400" />;
      case 'BETA_REJECTED':
        return <XCircleIcon className="w-6 h-6 text-red-400" />;
      case 'BETA_REQUIRED':
        return <ExclamationTriangleIcon className="w-6 h-6 text-cyan-400" />;
      default:
        return <ExclamationTriangleIcon className="w-6 h-6 text-slate-400" />;
    }
  };

  const getTitle = () => {
    switch (denial.code) {
      case 'BETA_PENDING':
        return 'Access Request Pending';
      case 'BETA_REJECTED':
        return 'Access Not Approved';
      case 'BETA_REQUIRED':
        return 'Beta Access Required';
      default:
        return 'Access Restricted';
    }
  };

  const getBgColor = () => {
    switch (denial.code) {
      case 'BETA_PENDING':
        return 'bg-yellow-500/10 border-yellow-500/30';
      case 'BETA_REJECTED':
        return 'bg-red-500/10 border-red-500/30';
      case 'BETA_REQUIRED':
        return 'bg-cyan-500/10 border-cyan-500/30';
      default:
        return 'bg-slate-500/10 border-slate-500/30';
    }
  };

  return (
    <div className={`${getBgColor()} border rounded-lg p-4 ${className}`}>
      <div className="flex items-start gap-3">
        {getIcon()}
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-white mb-1">{getTitle()}</h3>
          <p className="text-slate-300 text-sm">{denial.message}</p>
          {denial.code === 'BETA_REQUIRED' && (
            <a
              href="/beta"
              className="inline-block mt-3 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg transition-colors text-sm"
            >
              Request Access
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
